import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SystemNotificationService } from '@core/services/system-notification.service';
import { SystemNotification, SystemNotificationType } from '@models/system-notification.interface';

@Component({
  selector: 'app-system-notification-banner',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (service.hasNotifications()) {
      <div class="flex flex-col gap-1.5 px-1 pb-2 sm:px-3">
        @for (notification of service.notifications(); track notification.id) {
          <div
            class="flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-medium shadow-sm"
            [class]="bannerClass(notification.type)"
            role="alert"
          >
            <i [class]="iconClass(notification.type)" class="mt-0.5 shrink-0 text-base"></i>

            <div class="min-w-0 flex-1">
              <span class="font-semibold">{{ notification.title }}: </span>
              <span class="font-normal opacity-90">{{ notification.message }}</span>
            </div>

            <button
              type="button"
              class="shrink-0 rounded-lg p-1 opacity-60 transition-opacity hover:opacity-100 focus:outline-none"
              [attr.aria-label]="'Cerrar notificación: ' + notification.title"
              (click)="service.dismiss(notification.id)"
            >
              <i class="fa-solid fa-xmark text-base"></i>
            </button>
          </div>
        }
      </div>
    }
  `,
})
export class SystemNotificationBannerComponent {
  readonly service = inject(SystemNotificationService);

  bannerClass(type: SystemNotificationType): string {
    switch (type) {
      case 'warning':
        return 'bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100';
      case 'maintenance':
        return 'bg-rose-50 text-rose-900 dark:bg-rose-900/20 dark:text-rose-100';
      default:
        return 'bg-sky-100 text-sky-950 shadow-sky-900/10 dark:bg-blue-900/25 dark:text-blue-50';
    }
  }

  iconClass(type: SystemNotificationType): string {
    switch (type) {
      case 'warning':
        return 'fa-solid fa-triangle-exclamation text-amber-500';
      case 'maintenance':
        return 'fa-solid fa-screwdriver-wrench text-rose-500';
      default:
        return 'fa-solid fa-circle-info text-blue-600 dark:text-blue-300';
    }
  }
}
